import { useApi } from '@/api';
import { ChartTimeFilter, ExpandableKpiCard } from '@/components';
import { useChartFillDatasetsDates, useSparkColor } from '@/hooks';
import {
  ChartSkeleton,
  CurrencyValue,
  formatNumber,
  KpiCard,
  LineChart
} from '@blockanalitica/ui';
import { SimpleChartData } from '@blockanalitica/ui/package/types/src/components/charts/SimpleChart';
import { useCallback, useState } from 'react';

type HolderDataResponse = {
  wallet_address: string;
  balance: string;
  balance_change: string;
  address: string;
  historic: SimpleChartData[];
};

type HolderProps = {
  walletAddress: string;
};

export default function HolderAmountPage({ walletAddress }: HolderProps) {
  const color = useSparkColor();
  const [chartDaysAgo, setChartDaysAgo] = useState('30');

  const [data, error] = useApi<HolderDataResponse>({
    url: `/sparkstar/staking/holders/${walletAddress}/`,
    queryParams: {
      days_ago: chartDaysAgo
    }
  });
  const datasets = [
    {
      xField: 'date',
      yField: 'balance',
      borderColor: color
    }
  ];

  const chartData = useChartFillDatasetsDates(
    data?.historic ?? [],
    datasets,
    chartDaysAgo
  );

  const valueFormatter = useCallback((value: string | number) => {
    return <CurrencyValue value={value} variant="dynamic" />;
  }, []);

  const yScaleValueFormatter = (value: string | number) => {
    return formatNumber(Number(value));
  };

  if (error) {
    throw error;
  }

  if (!data) {
    return <ChartSkeleton />;
  }

  return (
    <ExpandableKpiCard
      text="Staked"
      defaultShowChart={true}
      hoverBorderColor={color}
      kpiCard={
        <KpiCard
          data={{
            balance: data['balance'],
            balance_change: data['balance_change']
          }}
          field="balance"
          options={{
            currency: {
              name: 'SPK',
              address: '0xc20059e0317de91738d13af027dfc4a50781b066'
            },
            valueOptions: {
              currencyValueOptions: {
                currencySize: '3'
              }
            }
          }}
          variant="nestedAsChild"
        />
      }>
      <LineChart
        data={chartData}
        datasets={datasets}
        valueFormatter={valueFormatter}
        showValuesInTooltip={true}
        yScaleValueFormatter={yScaleValueFormatter}
        tooltip={{
          valueFormatter: (value) => {
            return formatNumber(Number(value));
          },
          labelFormatter: () => 'Staked',
          displayColors: false
        }}
        timeFilter={
          <ChartTimeFilter
            activeOption={chartDaysAgo}
            onChange={setChartDaysAgo}
          />
        }
      />
    </ExpandableKpiCard>
  );
}
